import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
/* eslint-disable no-restricted-imports */
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { getFirebaseSingleton } from "@src/lib/firebase";
/* eslint-enable no-restricted-imports */
import { useEffect, useRef, useState } from "react";

interface UseSignOutForMagicLinkParams {
  isAuthorized: boolean;
}

interface UseSignOutForMagicLinkResult {
  /**
   * Whether a sign-out is currently in progress for magic link login.
   * When true, the page should show a loading state instead of redirecting.
   */
  isSigningOutForMagicLink: boolean;
  /**
   * Whether there's a magic link param in the URL that requires sign-out.
   * Used to prevent the normal authorized redirect while we handle sign-out.
   */
  hasMagicLinkParameter: boolean;
}

/**
 * Hook to handle signing out the current user when accessing the magic link page.
 *
 * This prevents issues where a support agent is logged into one worker's account
 * and uses a magic link for a different worker, causing requests to be made with
 * mismatched worker IDs and auth tokens.
 *
 * @see https://linear.app/clipboardhealth/issue/DEVOP-4705
 */
export function useSignOutForMagicLink(
  params: UseSignOutForMagicLinkParams
): UseSignOutForMagicLinkResult {
  const { isAuthorized } = params;
  const searchParams = useSearchParams<{ phoneNumber?: string }>();
  const phoneNumberFromUrl = searchParams.phoneNumber;

  const [isSigningOut, setIsSigningOut] = useState(false);
  const hasInitiatedSignOut = useRef(false);

  const hasMagicLinkParameter = Boolean(phoneNumberFromUrl);

  useEffect(() => {
    async function signOutCurrentUser() {
      // Only sign out if:
      // 1. User is currently logged in
      // 2. There's a magic link param
      // 3. We haven't already initiated sign-out
      if (!isAuthorized || !hasMagicLinkParameter || hasInitiatedSignOut.current) {
        return;
      }

      hasInitiatedSignOut.current = true;
      setIsSigningOut(true);

      try {
        const firebaseAuth = getFirebaseSingleton();
        const cognitoAuth = getCognitoSingleton();

        logEvent(APP_V2_APP_EVENTS.AUTO_LOGIN_CLEARING_EXISTING_AUTH, {
          hadFirebaseAuth: firebaseAuth.currentUser !== null,
          hadCognitoAuth: (await cognitoAuth.getCurrentUserInfo()) !== undefined,
        });

        // Sign out from both auth providers
        await Promise.all([firebaseAuth.signOut(), cognitoAuth.signOut()]);
      } catch (error) {
        logError(APP_V2_APP_EVENTS.AUTO_LOGIN_CLEAR_AUTH_FAILURE, { error });
        // Continue anyway - the sign-out will complete and the new login can proceed
      } finally {
        setIsSigningOut(false);
      }
    }

    void signOutCurrentUser();
  }, [isAuthorized, hasMagicLinkParameter]);

  return {
    isSigningOutForMagicLink: isSigningOut,
    hasMagicLinkParameter,
  };
}
